import { useState } from "react";
import { useToast } from "@/components/base/Toast";

type Step = 1 | 2 | 3;

interface Props {
  onClose: () => void;
  onConnected: () => void;
}

export function ConnectModal({ onClose, onConnected }: Props) {
  const { showToast } = useToast();
  const [step, setStep] = useState<Step>(1);
  const [otp, setOtp] = useState("");
  const [verifying, setVerifying] = useState(false);

  const steps: { num: Step; label: string }[] = [
    { num: 1, label: "Meta App Setup" },
    { num: 2, label: "Phone Verification" },
    { num: 3, label: "Test Connection" },
  ];

  const handleSendOtp = () => {
    showToast("Verification code sent to WhatsApp number", "success");
  };

  const handleVerify = () => {
    if (otp.length < 6) {
      showToast("Please enter a valid 6-digit code", "error");
      return;
    }
    setVerifying(true);
    setTimeout(() => {
      setVerifying(false);
      showToast("Phone verified successfully", "success");
      setStep(3);
    }, 800);
  };

  const handleFinish = () => {
    showToast("WhatsApp Business connected successfully!", "success");
    onConnected();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-lg">
        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-[#111827]">
            Connect WhatsApp Business API
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="px-6 pt-5 pb-2">
          <div className="flex items-center gap-0">
            {steps.map((s, i) => (
              <div key={s.num} className="flex items-center flex-1 last:flex-none">
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                      step > s.num
                        ? "bg-green-500 text-white"
                        : step === s.num
                        ? "bg-indigo-600 text-white"
                        : "bg-gray-200 text-[#6b7280]"
                    }`}
                  >
                    {step > s.num ? (
                      <i className="ri-check-line" />
                    ) : (
                      s.num
                    )}
                  </div>
                  <span
                    className={`text-[10px] font-medium mt-1.5 whitespace-nowrap ${
                      step >= s.num ? "text-[#374151]" : "text-[#9ca3af]"
                    }`}
                  >
                    {s.label}
                  </span>
                </div>
                {i < steps.length - 1 && (
                  <div
                    className={`h-0.5 flex-1 -mt-5 mx-1 rounded-full transition-colors ${
                      step > s.num ? "bg-green-500" : "bg-gray-200"
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step content */}
        <div className="px-6 py-5">
          {step === 1 && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                <p className="text-xs text-amber-800">
                  You need a <strong>Meta Business account</strong> and{" "}
                  <strong>WhatsApp Business API</strong> access to continue. Get these from the
                  Meta Business Manager.
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-[#6b7280] mb-1.5">
                  Meta App ID
                </label>
                <input
                  type="text"
                  placeholder="123456789012345"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-[#374151] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-[#6b7280] mb-1.5">
                  Meta App Secret
                </label>
                <input
                  type="password"
                  placeholder="Enter app secret"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-[#374151] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-[#6b7280] mb-1.5">
                  WhatsApp Business Account ID
                </label>
                <input
                  type="text"
                  placeholder="WABA ID from Meta"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-[#374151] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-[#6b7280] mb-1.5">
                  Phone Number ID
                </label>
                <input
                  type="text"
                  placeholder="Phone number ID"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-[#374151] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <a
                href="#"
                className="inline-block text-xs text-indigo-600 hover:text-indigo-700 font-medium whitespace-nowrap"
              >
                Get these from Meta Business Manager →
              </a>

              <button
                onClick={() => setStep(2)}
                className="w-full px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap"
              >
                Next
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-[#6b7280] mb-1.5">
                  WhatsApp Phone Number
                </label>
                <input
                  type="text"
                  value="+91 98765 43210"
                  readOnly
                  className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-[#374151]"
                />
              </div>

              <button
                onClick={handleSendOtp}
                className="w-full px-4 py-2.5 border border-indigo-600 text-indigo-600 text-sm font-medium rounded-lg hover:bg-indigo-50 transition-colors whitespace-nowrap"
              >
                Send Verification Code
              </button>

              <div>
                <label className="block text-xs font-medium text-[#6b7280] mb-1.5">
                  Enter 6-digit Code
                </label>
                <input
                  type="text"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-[#374151] text-center tracking-[0.5em] font-mono placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <button
                onClick={handleVerify}
                disabled={verifying}
                className="w-full px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-60 whitespace-nowrap"
              >
                {verifying ? "Verifying..." : "Verify"}
              </button>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5 text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center">
                <i className="ri-check-line text-3xl text-green-600" />
              </div>
              <div>
                <h4 className="text-base font-semibold text-[#111827]">
                  Connection Ready
                </h4>
                <p className="text-sm text-[#6b7280] mt-1">
                  WhatsApp Business API is configured and verified
                </p>
              </div>
              <button
                onClick={() => showToast("Test message sent to +91 98765 43210", "success")}
                className="w-full px-4 py-2.5 border border-indigo-600 text-indigo-600 text-sm font-medium rounded-lg hover:bg-indigo-50 transition-colors whitespace-nowrap"
              >
                Send Test Message
              </button>
              <button
                onClick={handleFinish}
                className="w-full px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}